/**
 * Authentic Kalocsai embroidery-inspired folk art SVG decorative components.
 * Matches the EXACT Kalocsai pattern from the SZESE race car livery.
 *
 * Kalocsai livery colors:
 *   Warm red/vermillion: #D94030
 *   Golden orange: #E8872A
 *   Soft cornflower blue: #6B9FD4
 *   Teal-green: #3B9B7A
 *   Dark teal-green: #2A7A5E
 *   Yellow-gold: #E8B830
 *
 * NO pink, NO magenta, NO purple, NO royal blue.
 *
 * Flower types: open poppy/rose, tulip buds, small daisies, blue accent flowers.
 * Style: symmetric, curved stems, berry dots, paired teal-green leaves.
 */

// ─── Shared sub-components (internal) ───────────────────────────────────────

/**
 * Large open Kalocsai flower — the most distinctive element.
 * 5 broad, rounded red outer petals + 4 orange inner petals + blue center.
 */
function KalocsaiFlower({
  x,
  y,
  scale = 1,
  rotation = 0,
  outerColor = "#D94030",
  innerColor = "#E8872A",
}: {
  x: number;
  y: number;
  scale?: number;
  rotation?: number;
  outerColor?: string;
  innerColor?: string;
}) {
  return (
    <g transform={`translate(${x}, ${y}) rotate(${rotation}) scale(${scale})`}>
      {/* 5 broad rounded outer petals (red) */}
      {[0, 72, 144, 216, 288].map((angle) => (
        <path
          key={angle}
          d="M0,-20 C-8,-18 -14,-8 -10,0 C-4,-4 4,-4 10,0 C14,-8 8,-18 0,-20Z"
          fill={outerColor}
          transform={`rotate(${angle})`}
        />
      ))}
      {/* 4 smaller inner petals (orange) */}
      {[45, 135, 225, 315].map((angle) => (
        <path
          key={angle}
          d="M0,-12 C-5,-10 -8,-4 -5,0 C-2,-2 2,-2 5,0 C8,-4 5,-10 0,-12Z"
          fill={innerColor}
          transform={`rotate(${angle})`}
        />
      ))}
      {/* Center */}
      <circle cx="0" cy="0" r="3" fill="#6B9FD4" />
      <circle cx="0" cy="0" r="1.5" fill="#E8B830" />
    </g>
  );
}

/** A classic Kalocsai tulip bud — red outer, orange inner peek */
function KalocsaiTulip({
  x,
  y,
  scale = 1,
  rotation = 0,
  petalColor = "#D94030",
  innerColor = "#E8872A",
}: {
  x: number;
  y: number;
  scale?: number;
  rotation?: number;
  petalColor?: string;
  innerColor?: string;
}) {
  return (
    <g transform={`translate(${x}, ${y}) rotate(${rotation}) scale(${scale})`}>
      {/* Center petal */}
      <path d="M0,0 C-4,-15 -3,-25 0,-35 C3,-25 4,-15 0,0Z" fill={petalColor} />
      {/* Left petal */}
      <path d="M0,0 C-8,-10 -18,-20 -14,-32 C-10,-28 -4,-18 0,0Z" fill={innerColor} />
      {/* Right petal */}
      <path d="M0,0 C8,-10 18,-20 14,-32 C10,-28 4,-18 0,0Z" fill={innerColor} />
      {/* Center dot */}
      <circle cx="0" cy="-8" r="2" fill="#E8B830" />
    </g>
  );
}

/** A small blue accent flower (5-petal round cluster) */
function BlueFlower({
  x,
  y,
  scale = 1,
  color = "#6B9FD4",
}: {
  x: number;
  y: number;
  scale?: number;
  color?: string;
}) {
  return (
    <g transform={`translate(${x}, ${y}) scale(${scale})`}>
      <circle cx="0" cy="-4" r="3" fill={color} opacity={0.9} />
      <circle cx="3.5" cy="-1" r="3" fill={color} opacity={0.9} />
      <circle cx="2" cy="3" r="3" fill={color} opacity={0.9} />
      <circle cx="-2" cy="3" r="3" fill={color} opacity={0.9} />
      <circle cx="-3.5" cy="-1" r="3" fill={color} opacity={0.9} />
      <circle cx="0" cy="0" r="1.5" fill="#E8B830" />
    </g>
  );
}

/** A small Kalocsai daisy — 8 short round petals with contrasting center */
function KalocsaiDaisy({
  x,
  y,
  scale = 1,
  color = "#D94030",
  centerColor = "#6B9FD4",
}: {
  x: number;
  y: number;
  scale?: number;
  color?: string;
  centerColor?: string;
}) {
  return (
    <g transform={`translate(${x}, ${y}) scale(${scale})`}>
      {[0, 45, 90, 135, 180, 225, 270, 315].map((angle) => (
        <path
          key={angle}
          d="M0,0 C-1.5,-4 -1,-8 0,-11 C1,-8 1.5,-4 0,0Z"
          fill={color}
          opacity={0.8}
          transform={`rotate(${angle})`}
        />
      ))}
      <circle cx="0" cy="0" r="2.5" fill={centerColor} />
    </g>
  );
}

/** A pointed leaf — teal-green, wider & more flowing, with optional vein */
function Leaf({
  x,
  y,
  rotation = 0,
  scale = 1,
  color = "#3B9B7A",
}: {
  x: number;
  y: number;
  rotation?: number;
  scale?: number;
  color?: string;
}) {
  return (
    <g transform={`translate(${x}, ${y}) rotate(${rotation}) scale(${scale})`}>
      <path d="M0,0 C-4,-5 -10,-9 -14,-4 C-10,-1 -4,1 0,0Z" fill={color} />
      {/* Darker center vein */}
      <path d="M-1,-1 L-11,-4" stroke="#2A7A5E" strokeWidth="0.5" opacity={0.6} fill="none" />
    </g>
  );
}

/** Berry cluster (3-5 small red dots) */
function BerryCluster({
  x,
  y,
  count = 3,
  color = "#D94030",
}: {
  x: number;
  y: number;
  count?: number;
  color?: string;
}) {
  const positions = [
    [0, 0],
    [4, -2],
    [-3, -3],
    [2, -5],
    [-1, -6],
  ].slice(0, count);
  return (
    <g transform={`translate(${x}, ${y})`}>
      {positions.map(([bx, by], i) => (
        <circle key={i} cx={bx} cy={by} r="2" fill={color} opacity={0.8} />
      ))}
    </g>
  );
}

/** Curling tendril at branch tips */
function Tendril({
  x,
  y,
  rotation = 0,
  color = "#E8872A",
}: {
  x: number;
  y: number;
  rotation?: number;
  color?: string;
}) {
  return (
    <g transform={`translate(${x}, ${y}) rotate(${rotation})`}>
      <path
        d="M0,0 C3,-2 5,-5 4,-8 C3,-10 1,-9 2,-7 C3,-5 4,-3 2,0"
        fill="none"
        stroke={color}
        strokeWidth="1"
        opacity={0.7}
      />
    </g>
  );
}

// ─── Public components ──────────────────────────────────────────────────────

interface FolkFlowerProps {
  size?: number;
  color?: string;
  opacity?: number;
  className?: string;
}

/**
 * A Kalocsai flower composition — central open flower with side tulip buds,
 * teal-green leaves, and berry accents. Symmetric.
 */
export function FolkFlower({
  size = 64,
  color = "#D94030",
  opacity = 0.1,
  className = "",
}: FolkFlowerProps) {
  return (
    <svg
      viewBox="0 0 100 100"
      width={size}
      height={size}
      className={className}
      style={{ opacity }}
      aria-hidden="true"
    >
      {/* Central open flower */}
      <KalocsaiFlower x={50} y={38} scale={0.9} outerColor={color} innerColor="#E8872A" />

      {/* Stem */}
      <path d="M50,48 C50,58 48,68 50,82" stroke="#2A7A5E" strokeWidth="2" fill="none" />

      {/* Leaves */}
      <Leaf x={50} y={58} rotation={-20} scale={1.3} />
      <Leaf x={50} y={64} rotation={200} scale={1.3} />

      {/* Side buds — small tulips */}
      <KalocsaiTulip
        x={30}
        y={55}
        scale={0.5}
        rotation={-25}
        petalColor={color}
        innerColor="#E8872A"
      />
      <path d="M30,55 C35,60 45,65 50,68" stroke="#2A7A5E" strokeWidth="1.2" fill="none" />

      <KalocsaiTulip
        x={70}
        y={55}
        scale={0.5}
        rotation={25}
        petalColor={color}
        innerColor="#E8872A"
      />
      <path d="M70,55 C65,60 55,65 50,68" stroke="#2A7A5E" strokeWidth="1.2" fill="none" />

      {/* Berry accents */}
      <BerryCluster x={25} y={65} count={3} color={color} />
      <BerryCluster x={73} y={65} count={3} color={color} />

      {/* Tendrils */}
      <Tendril x={22} y={60} rotation={-30} />
      <Tendril x={78} y={60} rotation={30} color="#D94030" />
    </svg>
  );
}

interface FolkBranchProps {
  className?: string;
  opacity?: number;
  width?: number;
  height?: number;
}

/**
 * A small decorative sprig / branch with curved stem, leaves,
 * a flower, blue accents, and berries. Good for edge decoration.
 */
export function FolkBranch({
  className = "",
  opacity = 0.12,
  width = 260,
  height = 100,
}: FolkBranchProps) {
  return (
    <svg
      viewBox="0 0 260 100"
      width={width}
      height={height}
      className={className}
      style={{ opacity }}
      aria-hidden="true"
    >
      {/* Main curved stem (S-curve) */}
      <path
        d="M20,80 C50,70 80,30 130,35 C180,40 210,70 240,60"
        stroke="#2A7A5E"
        strokeWidth="2.5"
        fill="none"
      />

      {/* Large open flower at the left */}
      <KalocsaiFlower x={30} y={68} scale={0.7} rotation={-15} />

      {/* Blue flower cluster at the curve peak */}
      <BlueFlower x={100} y={28} scale={1} />
      <BlueFlower x={115} y={24} scale={0.7} />

      {/* Small orange daisy near right */}
      <KalocsaiDaisy x={200} y={62} scale={0.9} color="#E8872A" centerColor="#E8B830" />

      {/* Tulip bud at right end */}
      <KalocsaiTulip x={235} y={52} scale={0.7} rotation={10} />

      {/* Leaves along the stem */}
      <Leaf x={55} y={58} rotation={-40} scale={1.3} />
      <Leaf x={65} y={50} rotation={160} scale={1.1} />
      <Leaf x={150} y={38} rotation={-25} scale={1.2} />
      <Leaf x={160} y={44} rotation={170} scale={1} />
      <Leaf x={220} y={58} rotation={-10} scale={1.1} />

      {/* Berry clusters */}
      <BerryCluster x={80} y={40} count={3} color="#D94030" />
      <BerryCluster x={180} y={50} count={4} color="#D94030" />

      {/* Tendrils */}
      <Tendril x={245} y={55} rotation={15} />
      <Tendril x={15} y={75} rotation={-40} color="#D94030" />
    </svg>
  );
}

interface FolkBorderProps {
  className?: string;
  opacity?: number;
}

/**
 * Horizontal decorative border — repeating Kalocsai pattern:
 * open flowers, tulips, blue accents, curved stems, leaves, berries.
 * Uses the livery Kalocsai palette. ~50px tall, tiles across full width.
 */
export function FolkBorder({ className = "", opacity = 0.08 }: FolkBorderProps) {
  return (
    <div className={`w-full overflow-hidden ${className}`} aria-hidden="true">
      <svg
        viewBox="0 0 1200 50"
        className="w-full h-10"
        preserveAspectRatio="xMidYMid meet"
        style={{ opacity }}
      >
        {Array.from({ length: 6 }).map((_, i) => {
          const xOff = i * 200;
          return (
            <g key={i} transform={`translate(${xOff}, 0)`}>
              {/* Curved vine stem (S-curve per unit) */}
              <path
                d="M0,25 C25,8 50,8 75,20 C100,32 125,42 150,25 C175,8 185,12 200,25"
                fill="none"
                stroke="#2A7A5E"
                strokeWidth="1.8"
              />

              {/* Open flower at the first peak */}
              <KalocsaiFlower x={38} y={12} scale={0.35} />

              {/* Blue accent flower at the valley */}
              <BlueFlower x={112} y={36} scale={0.6} />

              {/* Tulip bud at second peak */}
              <KalocsaiTulip x={162} y={14} scale={0.35} />

              {/* Leaves */}
              <Leaf x={55} y={14} rotation={-30} scale={0.9} />
              <Leaf x={65} y={20} rotation={160} scale={0.7} />
              <Leaf x={130} y={30} rotation={-20} scale={0.8} />
              <Leaf x={140} y={35} rotation={170} scale={0.7} />
              <Leaf x={185} y={18} rotation={-15} scale={0.7} />

              {/* Berry dots */}
              <circle cx={88} cy={22} r="1.8" fill="#D94030" opacity={0.7} />
              <circle cx={92} cy={18} r="1.5" fill="#D94030" opacity={0.6} />
              <circle cx={20} cy={20} r="1.5" fill="#E8872A" opacity={0.7} />
              <circle cx={175} cy={20} r="1.5" fill="#E8872A" opacity={0.6} />
            </g>
          );
        })}
      </svg>
    </div>
  );
}

interface FolkCornerProps {
  className?: string;
  opacity?: number;
  size?: number;
  /** Which corner: top-left, top-right, bottom-left, bottom-right */
  position?: "top-left" | "top-right" | "bottom-left" | "bottom-right";
}

/**
 * An L-shaped corner decoration — like the corner of a Kalocsai tablecloth.
 * Main curved stem with open flowers, tulips, blue accents, leaves, and berries.
 * ~220px in each direction. Mirrors/rotates based on `position`.
 */
export function FolkCorner({
  className = "",
  opacity = 0.08,
  size = 220,
  position = "top-left",
}: FolkCornerProps) {
  const transforms: Record<string, string> = {
    "top-left": "scale(1, 1)",
    "top-right": "scale(-1, 1)",
    "bottom-left": "scale(1, -1)",
    "bottom-right": "scale(-1, -1)",
  };

  const positionClasses: Record<string, string> = {
    "top-left": "top-0 left-0",
    "top-right": "top-0 right-0",
    "bottom-left": "bottom-0 left-0",
    "bottom-right": "bottom-0 right-0",
  };

  return (
    <div
      className={`absolute pointer-events-none ${positionClasses[position]} ${className}`}
      aria-hidden="true"
    >
      <svg
        viewBox="0 0 220 220"
        width={size}
        height={size}
        style={{ opacity, transform: transforms[position] }}
      >
        {/* Main L-shaped curved stem — from left edge curving to top edge */}
        <path
          d="M5,200 C15,170 25,140 45,115 C65,90 85,75 105,65 C125,55 155,40 195,10"
          fill="none"
          stroke="#2A7A5E"
          strokeWidth="2.2"
        />

        {/* Secondary stem branching inward */}
        <path
          d="M10,150 C30,130 50,105 75,85 C95,70 115,55 145,15"
          fill="none"
          stroke="#2A7A5E"
          strokeWidth="1.4"
          opacity={0.7}
        />

        {/* Tertiary small branch */}
        <path
          d="M30,120 C40,110 55,95 70,80"
          fill="none"
          stroke="#3B9B7A"
          strokeWidth="1"
          opacity={0.5}
        />

        {/* ── Large open flower at the bend ── */}
        <KalocsaiFlower x={80} y={78} scale={0.7} rotation={-15} />

        {/* ── Tulip bud near the top ── */}
        <KalocsaiTulip x={155} y={28} scale={0.65} rotation={10} />

        {/* ── Small tulip near bottom-left ── */}
        <KalocsaiTulip x={25} y={165} scale={0.55} rotation={-30} />

        {/* ── Blue flower clusters ── */}
        <BlueFlower x={120} y={50} scale={0.85} />
        <BlueFlower x={55} y={100} scale={0.7} />

        {/* ── Small orange daisy ── */}
        <KalocsaiDaisy x={100} y={55} scale={0.6} color="#E8872A" centerColor="#E8B830" />

        {/* ── Leaves along main stem ── */}
        <Leaf x={20} y={180} rotation={-55} scale={1.3} />
        <Leaf x={35} y={155} rotation={130} scale={1.1} />
        <Leaf x={55} y={108} rotation={-40} scale={1.2} />
        <Leaf x={95} y={72} rotation={150} scale={1} />
        <Leaf x={130} y={48} rotation={-30} scale={1.1} />
        <Leaf x={170} y={25} rotation={-20} scale={1} />

        {/* ── Leaves along secondary stem ── */}
        <Leaf x={40} y={125} rotation={40} scale={0.9} />
        <Leaf x={85} y={80} rotation={-55} scale={0.8} />
        <Leaf x={115} y={45} rotation={155} scale={0.8} />

        {/* ── Berry clusters ── */}
        <BerryCluster x={15} y={190} count={3} color="#D94030" />
        <BerryCluster x={60} y={95} count={3} color="#E8872A" />
        <BerryCluster x={140} y={35} count={4} color="#D94030" />
        <BerryCluster x={180} y={15} count={3} color="#D94030" />

        {/* ── Tendrils ── */}
        <Tendril x={195} y={8} rotation={20} />
        <Tendril x={5} y={195} rotation={-50} color="#D94030" />

        {/* ── Lone berry dots for rhythm ── */}
        <circle cx={45} cy={135} r="2" fill="#D94030" opacity={0.6} />
        <circle cx={110} cy={60} r="1.8" fill="#E8872A" opacity={0.5} />
        <circle cx={165} cy={20} r="1.5" fill="#D94030" opacity={0.5} />
      </svg>
    </div>
  );
}

/** A thin Hungarian tricolor stripe — red/white/green */
export function TricolorStripe({
  className = "",
  orientation = "horizontal",
}: {
  className?: string;
  orientation?: "horizontal" | "vertical";
}) {
  if (orientation === "vertical") {
    return (
      <div className={`flex flex-col w-1 ${className}`} aria-hidden="true">
        <div className="flex-1 bg-[#D94030]" />
        <div className="flex-1 bg-white" />
        <div className="flex-1 bg-[#3B9B7A]" />
      </div>
    );
  }

  return (
    <div className={`flex h-0.5 w-full ${className}`} aria-hidden="true">
      <div className="flex-1 bg-[#D94030]" />
      <div className="flex-1 bg-white" />
      <div className="flex-1 bg-[#3B9B7A]" />
    </div>
  );
}
